import React, { useMemo, useState } from 'react';
import { resolveCampaignDateRange } from '../../../../../../../core/utils/campaignDateRange';
import { ShoppingCart, DollarSign, Package } from 'lucide-react';

import CampaignRangeSelector from '../../../components/CampaignRangeSelector';
import BasketItemsChart from './BasketItemsChart';
import PerformanceCardSkeleton from '../../../components/PerformanceCardSkeleton';
import { formatCurrency2SigFig, formatWholeNumberNaN } from '../../../../../../../core/utils/campaigns';
import { useTimezone } from '../../../../../../../core/hooks/useTimezone';
import KpiBar, { KpiCard } from '../../../../../../shared/designSystem/components/KpiBar';

// =============================================================================
// BasketItemsPerformance — unified Performance card for the V2 Basket Items
// tab. One section: head (eyebrow + range selector), 3-column KPI bar
// (Transactions / Sub Total / Quantity), trend chart. Mirrors the Summary /
// Display / Transactions / SignUps Performance pattern.
//
// Sub Total carries the "Revenue before taxes, shipping and promotions"
// tooltip that v1 put next to the KPI title via an ExtraInfo badge.
// =============================================================================

const BasketItemsPerformance = ({
  detailBarSummary = { transactions: 0, revenue: 0, quantity: 0 },
  byDate,
  dateRange,
  onDateChange,
  campaignOrder,
  loading = false,
}) => {
  const { timezone: effectiveTimezone = 'UTC' } = useTimezone();
  const [rangeType, setRangeType] = useState('Custom');

  // Default range for the Custom pill fallback label — data's own min/max
  // if available, else campaign flight dates.
  // Campaign flight start → today (see core/utils/campaignDateRange). Also
  // fixes a tz shift: both the data extent and the flight fallback read
  // UTC-stored dates in the viewer's zone, moving the window a day west of UTC.
  const defaultDateRange = useMemo(() => {
    return resolveCampaignDateRange(
      campaignOrder,
      (byDate || []).map((d) => d && d.date)
    );
  }, [byDate, campaignOrder]);

  if (loading) return <PerformanceCardSkeleton />;

  return (
    <section className="campaign-overview__performance">
      <header className="campaign-overview__performance-head">
        <div className="campaign-overview__performance-eyebrow mono-eyebrow">
          Performance
        </div>
        <CampaignRangeSelector
          rangeType={rangeType}
          byCampaignRangeDate={dateRange}
          setRangeType={setRangeType}
          setByCampaignRangeDate={(newRange) => {
            if (onDateChange) onDateChange(newRange);
          }}
          timezone={effectiveTimezone}
          defaultDateRange={defaultDateRange}
        />
      </header>

      <KpiBar>
        <KpiCard
          icon={<ShoppingCart size={16} />}
          iconTint="primary"
          label="Transactions"
          value={formatWholeNumberNaN(detailBarSummary.transactions)}
        />
        <KpiCard
          icon={<DollarSign size={16} />}
          iconTint="emerald"
          label="Sub Total"
          labelTooltip="Revenue before taxes, shipping and promotions"
          value={formatCurrency2SigFig(detailBarSummary.revenue)}
        />
        <KpiCard
          icon={<Package size={16} />}
          iconTint="indigo"
          label="Quantity"
          labelTooltip="Total number of product items within all selected transactions"
          value={formatWholeNumberNaN(detailBarSummary.quantity)}
        />
      </KpiBar>

      <BasketItemsChart byDate={byDate} />
    </section>
  );
};

export default BasketItemsPerformance;
